/**
 * Component Registry - Maps screenName from API to React Components
 */

// Import centralized routes helper
import { getComponentByRoutePath } from './CentralizedRoutes';

// Admin Components
import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminTicketsList from '../pages/admin/AdminTicketsList';
import AdminRegister from '../pages/admin/Register';
import CategoryManagement from '../pages/admin/CategoryManagement';
import InquiryTypeManagement from '../pages/admin/InquiryTypeManagement';
import InquiryCategoryManagement from '../pages/admin/InquiryCategoryManagement';
import InquiryPriorityLevelManagement from '../pages/admin/InquiryPriorityLevelManagement';
import InquiryEscalationLevelManagement from '../pages/admin/InquiryEscalationLevelManagement';
import InquirySubCategoryManagement from '../pages/admin/InquirySubCategoryManagement';
import InquiryEscalationMatrixManagement from '../pages/admin/InquiryEscalationMatrixManagement';
import GracePeriodConfigurationManagement from '../pages/admin/GracePeriodConfigurationManagement';
import WorkingHoursManagement from '../pages/admin/WorkingHoursManagement';
import InquiryResponsibleRoleCapacityManagement from '../pages/admin/InquiryResponsibleRoleCapacityManagement';
import UserManagement from '../pages/admin/UserManagement';
import RoleManagement from '../pages/admin/RoleManagement';
import EmployeeHistory from '../pages/admin/EmployeeHistory';
import SlaBreachedReport from '../pages/admin/SlaBreachedReport';
import ListOfTicketsReport from '../pages/admin/ListOfTicketsReport';
import AgentsReport from '../pages/admin/AgentsReport';
import SourceWiseTicketsReport from '../pages/admin/SourceWiseTicketsReport';
import AgentWiseSLABreachReport from '../pages/admin/AgentWiseSLABreachReport';
import AttendanceView from '../pages/admin/AttendanceView';
import UnifiedSLAManagement from '../pages/admin/UnifiedSLAManagement';
import EscalatedTicketsManagement from '../pages/escalated';
import UnifiedWorkflow from '../pages/admin/UnifiedWorkflow';
import AdministrationContainer from '../modules/administration/AdministrationContainer';
import UserGuide from '../components/UserGuide';

// Agent Components
import AgentDashboard from '../pages/agent/AgentDashboard';
import UnassignedTickets from '../pages/agent/tickets/UnassignedTickets';
import AgentMyTickets from '../pages/agent/tickets/MyTickets';
import TeamTickets from '../pages/agent/tickets/TeamTickets';
import EscalatedTickets from '../pages/agent/tickets/EscalatedTickets';
import ClosedTickets from '../pages/agent/tickets/ClosedTickets';
import MyArticlesScreen from '../pages/agent/knowledge/MyArticlesScreen';
import UserGuideScreen from '../pages/agent/knowledge/UserGuideScreen';
import ReferenceCustomerLogin from '../pages/agent/integratedmodule/ReferenceCustomerLogin';
import MyTicketsOverview from '../pages/agent/reports/MyTicketsOverview';

// Portal Components
import UnifiedPortalDashboard from '../pages/portal/UnifiedPortalDashboard';
import UnifiedKnowledgeBase from '../pages/portal/UnifiedKnowledgeBase';
import UnifiedProfile from '../pages/portal/UnifiedProfile';
import MyTickets from '../pages/portal/tickets/MyTickets';
import CreateTicketForm from '../pages/portal/tickets/CreateTicketForm';

// Super Admin Components
import SuperAdminDashboard from '../pages/super-admin/SuperAdminDashboard';
import OrganizationalManagement from '../pages/super-admin/OrganizationalManagement';
import GlobalSettings from '../pages/super-admin/GlobalSettings';
import SuperAdminRegister from '../pages/super-admin/Register';
import SystemMonitoring from '../pages/super-admin/SystemMonitoring';
import LicenseManagement from '../pages/super-admin/LicenseManagement';
import EmergencyActions from '../pages/super-admin/EmergencyActions';
import UnifiedBilling from '../pages/super-admin/UnifiedBilling';
import AIConfigurationDashboard from '../pages/super-admin/ai/AIConfigurationDashboard';
import AIPerformanceMonitoring from '../pages/super-admin/ai/AIPerformanceMonitoring';
import ModelManagement from '../pages/super-admin/ai/ModelManagement';
import AutomationRules from '../pages/super-admin/ai/AutomationRules';
import AIAnalytics from '../pages/super-admin/ai/AIAnalytics';
import AISettings from '../pages/super-admin/ai/AISettings';
import AuditorDashboard from '../pages/super-admin/audit/AuditorDashboard';
import ComplianceReporting from '../pages/super-admin/audit/ComplianceReporting';
import ComplianceReports from '../pages/super-admin/audit/ComplianceReports';
import AuditFindings from '../pages/super-admin/audit/AuditFindings';
import AuditFindingsManagement from '../pages/super-admin/audit/AuditFindingsManagement';
import SuperAdminProfileManagement from '../pages/super-admin/profile/SuperAdminProfileManagement';
import MasterFeaturesManagement from '../pages/super-admin/MasterFeaturesManagement';

// Public pages
import PrivacyPolicy from '../pages/public/PrivacyPolicy';

/**
 * Component Registry Map
 * Key: screenName from API (case-insensitive)
 * Value: React Component
 */
export const COMPONENT_REGISTRY = {
  // Public / landing
  'Privacy Policy': PrivacyPolicy,

  // Dashboards (route-based selection in getComponent function)
  'Admin Dashboard': AdminDashboard,
  'Agent Dashboard': AgentDashboard,
  'Portal Dashboard': UnifiedPortalDashboard,
  'Super Admin Dashboard': SuperAdminDashboard,
  
  // Admin
  'User Login Reports': EmployeeHistory,
  'SLA Breached Reports': SlaBreachedReport,
  'List of Tickets Report': ListOfTicketsReport,
  'Agents Report': AgentsReport,
  'Source wise Tickets Report': SourceWiseTicketsReport,
  'Agent wise SLA Breach Report': AgentWiseSLABreachReport,
  'Attendance View': AttendanceView,
  'Attendance': AttendanceView,
  'Monthly Attendance': AttendanceView,
  'SLA Policies': UnifiedSLAManagement,
  'Escalated': EscalatedTicketsManagement,
  'Workflow': UnifiedWorkflow,
  'Module Creation': AdministrationContainer,
  'Screen Creation': AdministrationContainer,
  'Register': AdminRegister, // Default to admin register (for admin context)
  'Categories': CategoryManagement,
  'Inquiry Types': InquiryTypeManagement,
  'Inquiry Type Master': InquiryTypeManagement,
  'Inquiry Categories': InquiryCategoryManagement,
  'Inquiry Category Master': InquiryCategoryManagement,
  'Inquiry Priority Levels': InquiryPriorityLevelManagement,
  'Inquiry Priority Level Master': InquiryPriorityLevelManagement,
  'Inquiry Escalation Levels': InquiryEscalationLevelManagement,
  'Inquiry Escalation Level Master': InquiryEscalationLevelManagement,
  'Inquiry Sub Categories': InquirySubCategoryManagement,
  'Inquiry Sub Category Master': InquirySubCategoryManagement,
  'Inquiry Escalation Matrix': InquiryEscalationMatrixManagement,
  'Inquiry Escalation Matrix Master': InquiryEscalationMatrixManagement,
  'Grace Period Configurations': GracePeriodConfigurationManagement,
  'Grace Period Configuration': GracePeriodConfigurationManagement,
  'Working Hours': WorkingHoursManagement,
  'Inquiry Role Capacity': InquiryResponsibleRoleCapacityManagement,
  'Inquiry Responsible Role Capacity': InquiryResponsibleRoleCapacityManagement,
  'Roles and Privileges': RoleManagement,
  'User Guide': UserGuide,
  
  // Agent
  'Unassigned Tickets': UnassignedTickets,
  'My Tickets': AgentMyTickets, // Default: Agent version (assigned tickets) - will be overridden for portal routes in getComponent()
  'Team Tickets': TeamTickets,
  'Escalated Tickets': EscalatedTickets,
  'Closed Tickets': ClosedTickets,
  'My Articles': MyArticlesScreen,
  'Reference Customer Login': ReferenceCustomerLogin,
  'My Tickets Overview': MyTicketsOverview,
  
  // Portal (Customer) - Use alternative names to avoid key conflicts
  'Customer Tickets': MyTickets, // Portal version (customer's own tickets)
  'Tickets': MyTickets, // Generic name for portal
  'Knowledge Base': UnifiedKnowledgeBase,
  'Customer Profile': UnifiedProfile, // Portal profile
  'Create Ticket': CreateTicketForm,
  
  // Super Admin (only super-admin specific screens)
  'Organizational Management': OrganizationalManagement,
  'Master Features': MasterFeaturesManagement,
  'Master Features Management': MasterFeaturesManagement,
  'Register User': SuperAdminRegister,
  'Global Settings': GlobalSettings,
  'System Monitoring': SystemMonitoring,
  'License Management': LicenseManagement,
  'Emergency Actions': EmergencyActions,
  'Billing': UnifiedBilling,
  'Billing Dashboard': UnifiedBilling,
  'AI Orchestrator': AIConfigurationDashboard,
  'AI Dashboard': AIConfigurationDashboard, // Alias for AI Configuration Dashboard
  'AI Configuration Dashboard': AIConfigurationDashboard,
  'AI Performance Monitoring': AIPerformanceMonitoring,
  'AI Performance': AIPerformanceMonitoring,
  'Model Management': ModelManagement,
  'Models': ModelManagement,
  'Automation Rules': AutomationRules,
  'Automation': AutomationRules,
  'AI Analytics': AIAnalytics,
  'Analytics': AIAnalytics,
  'AI Settings': AISettings,
  'Auditor': AuditorDashboard,
  'Auditor Dashboard': AuditorDashboard,
  'Compliance Reporting': ComplianceReporting,
  'Compliance': ComplianceReporting,
  'Compliance Reports': ComplianceReports,
  'Audit Findings': AuditFindings,
  'Findings': AuditFindings,
  'Audit Findings Management': AuditFindingsManagement,
  'Findings Management': AuditFindingsManagement,
  'Super Admin Profile': SuperAdminProfileManagement,
};

/**
 * Get component by screenName
 * @param {string} screenName - The screen name from API
 * @param {string} route - Optional route path for context (to distinguish portal vs agent)
 */
export const getComponent = (screenName, route = null) => {
  if (!screenName) return null;
  
  // Route-based component selection (prioritize by route context)
  if (route) {
    // Super-admin routes
    if (route.startsWith('/super-admin')) {
      if (screenName === 'Dashboard' || screenName === 'Super Admin Dashboard') {
        return SuperAdminDashboard;
      }
      if (screenName === 'Register' || screenName === 'Register User') {
        return SuperAdminRegister;
      }
      if (screenName === 'Profile') {
        return SuperAdminProfileManagement;
      }
      // Try exact match for super-admin screens
      if (COMPONENT_REGISTRY[screenName]) {
        return COMPONENT_REGISTRY[screenName];
      }
    }
    
    // Admin routes
    if (route.startsWith('/admin')) {
      if (screenName === 'Dashboard' || screenName === 'Admin Dashboard') {
        return AdminDashboard;
      }
      if (screenName === 'Register') {
        return AdminRegister;
      }
    }
    
    // Portal routes
    if (route.startsWith('/portal')) {
      if (screenName === 'Dashboard' || screenName === 'Portal Dashboard' || screenName === 'Customer Dashboard') {
        return UnifiedPortalDashboard;
      }
      if (screenName === 'My Tickets' || screenName === 'Customer Tickets' || screenName === 'Tickets') {
        return MyTickets;
      }
      if (screenName === 'Knowledge Base') {
        return UnifiedKnowledgeBase;
      }
      if (screenName === 'Profile') {
        return UnifiedProfile;
      }
      if (screenName === 'Create Ticket') {
        return CreateTicketForm;
      }
    }
  }
  
  // If route is provided and it's an agent route, prioritize agent components
  if (route && route.startsWith('/agent')) {
    if (screenName === 'My Tickets') {
      return AgentMyTickets; // Agent version
    }
  }
  
  // Exact match
  if (COMPONENT_REGISTRY[screenName]) {
    return COMPONENT_REGISTRY[screenName];
  }
  
  // Case-insensitive match
  const lower = screenName.toLowerCase();
  for (const [key, component] of Object.entries(COMPONENT_REGISTRY)) {
    if (key.toLowerCase() === lower) {
      return component;
    }
  }
  
  // Partial match
  for (const [key, component] of Object.entries(COMPONENT_REGISTRY)) {
    if (key.toLowerCase().includes(lower) || lower.includes(key.toLowerCase())) {
      return component;
    }
  }
  
  console.warn(`Component not found for: ${screenName}`);
  return null;
};

/**
 * Get component by route
 * Now uses centralized routes configuration
 */
export const getComponentByRoute = (routing) => {
  if (!routing) return null;
  return getComponentByRoutePath(routing);
};

export default COMPONENT_REGISTRY;
